import { useState } from "react";
import { X } from "lucide-react";
import { theme } from "../../../utils/theme";

const CurrentSpecialsCard = ({
    form,
    setForm,
    isEditing,
    onEdit,
    onCancel,
    onSave,
    type = "printShop", // printShop | supplier | specialist
}) => {
    const t = theme[type];

    const [title, setTitle] = useState("");
    const [details, setDetails] = useState("");
    const [badgeLabel, setBadgeLabel] = useState("");

    const currentSpecials = form.currentSpecials || [];

    // REMOVE Special
    const removeSpecial = (indexToRemove) => {
        setForm((prev) => ({
            ...prev,
            currentSpecials: prev.currentSpecials.filter(
                (_, index) => index !== indexToRemove
            ),
        }));
    };

    // ADD Specials
    const addSpecial = () => {
        if (!title.trim() || !details.trim() || !badgeLabel.trim()) return;

        const newSpecial = {
            title: title.trim(),
            details: details.trim(),
            badgeLabel: badgeLabel.trim(),
        };

        setForm((prev) => ({
            ...prev,
            currentSpecials: [...(prev.currentSpecials || []), newSpecial],
        }));

        setTitle("");
        setDetails("");
        setBadgeLabel("");
    };

    return (
        <div className="bg-white rounded-[16px] border border-[#e4e5e7] overflow-hidden">

            {/* HEADER */}
            <div className="px-[20px] py-[14px] border-b border-[#f0f1f3]">
                <div className="flex items-center justify-between">
                    <p className="font-semibold text-[15px] text-[#212326]">
                        Current Specials
                    </p>

                    {!isEditing ? (
                        <button
                            onClick={onEdit}
                            className="px-[14px] py-[7px] rounded-[10px] font-semibold text-[13px] text-white"
                            style={{ backgroundColor: t.primary }}
                        >
                            Edit
                        </button>
                    ) : (
                        <div className="flex items-center gap-[8px]">
                            <button
                                onClick={onCancel}
                                className="px-[14px] py-[7px] rounded-[10px] font-semibold text-[13px]"
                                style={{
                                    backgroundColor: t.accentBg,
                                    color: t.primary,
                                }}
                            >
                                Cancel
                            </button>

                            <button
                                onClick={onSave}
                                className="px-[14px] py-[7px] rounded-[10px] font-semibold text-[13px] text-white"
                                style={{ backgroundColor: t.primary }}
                            >
                                Save
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* VIEW MODE */}
            {!isEditing && (
                <div className="p-[20px] flex flex-wrap gap-[8px]">
                    {currentSpecials.length === 0 && (
                        <p className="font-medium text-[14px] text-[#717182]">
                            No specials listed yet — add one to display it on your marketplace card.
                        </p>
                    )}

                    <div className="flex flex-col gap-[10px] mb-[12px] w-full">
                        {currentSpecials.map((item, index) => (
                            <div
                                key={index}
                                className="border border-[#FFE0D6] w-full rounded-[12px] p-[12px] flex items-start justify-between gap-[12px]"
                                style={{ backgroundColor: t.accentBg }}
                            >
                                {/* LEFT ICON */}
                                <div className="flex-shrink-0 mt-[2px]">
                                    <svg
                                        width="10"
                                        height="13"
                                        viewBox="0 0 10 13"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M2.62565 7.29171C3.01243 7.29171 3.38336 7.13806 3.65685 6.86457C3.93034 6.59108 4.08398 6.22015 4.08398 5.83337C4.08398 5.02837 3.79232 4.66671 3.50065 4.08337C2.87532 2.83329 3.36998 1.71854 4.66732 0.583374C4.95898 2.04171 5.83398 3.44171 7.00065 4.37504C8.16732 5.30837 8.75065 6.41671 8.75065 7.58337C8.75065 8.1196 8.64503 8.65058 8.43983 9.146C8.23462 9.64141 7.93384 10.0916 7.55467 10.4707C7.1755 10.8499 6.72535 11.1507 6.22994 11.3559C5.73453 11.5611 5.20355 11.6667 4.66732 11.6667C4.13109 11.6667 3.60011 11.5611 3.10469 11.3559C2.60928 11.1507 2.15914 10.8499 1.77996 10.4707C1.40079 10.0916 1.10002 9.64141 0.89481 9.146C0.689603 8.65058 0.583984 8.1196 0.583984 7.58337C0.583984 6.91079 0.836568 6.24521 1.16732 5.83337C1.16732 6.22015 1.32096 6.59108 1.59445 6.86457C1.86794 7.13806 2.23888 7.29171 2.62565 7.29171Z"
                                            stroke="#FF5323"
                                            strokeWidth="1.16667"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </div>

                                {/* CENTER CONTENT */}
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-[14px] text-[#212326] leading-[18px]">
                                        {item.title}
                                    </p>

                                    <p className="text-[13px] text-[#717182] mt-[4px] leading-[18px]">
                                        {item.details}
                                    </p>
                                </div>

                                {/* RIGHT BADGE */}
                                <div className="flex-shrink-0">
                                    <span
                                        className="px-[8px] py-[3px] rounded-[8px] text-[11px] font-semibold"
                                        style={{
                                            backgroundColor: t.primary,
                                            color: "#fff",
                                        }}
                                    >
                                        {item.badgeLabel}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* EDIT MODE */}
            {isEditing && (
                <div className="p-[20px]">

                    {/* TAGS */}
                    <div className="flex flex-wrap gap-[8px] mb-[12px]">
                        {currentSpecials.map((item, index) => (
                            <span
                                key={index}
                                className="pl-[12px] pr-[6px] py-[6px] rounded-[10px] font-medium text-[13px] flex items-center gap-[6px]"
                                style={{
                                    backgroundColor: t.accentBg,
                                    color: t.primary,
                                }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                    class="lucide lucide-printer w-[12px] h-[12px]">
                                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                                    <path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6"></path>
                                    <rect x="6" y="14" width="12" height="8" rx="1"></rect>
                                </svg>
                                {item.title}
                                <button
                                    onClick={() => removeSpecial(item)}
                                    className="w-[18px] h-[18px] rounded-full flex items-center justify-center"
                                    style={{ backgroundColor: "rgba(0,0,0,0.05)" }}
                                >
                                    <X className="w-[10px] h-[10px]" />
                                </button>
                            </span>
                        ))}


                    </div>

                    {/* ADD SPECIAL */}
                    <div className="border-2 border-dashed border-[#d9dce1] rounded-[12px] p-[16px] mt-[16px]">
                        <p className="text-[13px] font-semibold text-[#212326] mb-[12px]">
                            Add a special
                        </p>

                        <div className="flex flex-col gap-[10px]">
                            <input
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Title (e.g. Bulk Flyer Deal)"
                                className={`h-[40px] rounded-[10px] border border-[#e4e5e7] focus:border-[${t.primary}] px-[14px] font-medium text-[13px] text-[#212326] placeholder:text-[#9298a0] focus:outline-none`}
                            />

                            <input
                                value={details}
                                onChange={(e) => setDetails(e.target.value)}
                                placeholder="Details (e.g. 1 000 A5 flyers from R650)"
                                className={`h-[40px] rounded-[10px] border border-[#e4e5e7] focus:border-[${t.primary}] px-[14px] font-medium text-[13px] text-[#212326] placeholder:text-[#9298a0] focus:outline-none`}
                            />

                            <div className="flex gap-[8px]">
                                <input
                                    value={badgeLabel}
                                    onChange={(e) => setBadgeLabel(e.target.value)}
                                    placeholder="Badge label (e.g. This week)"
                                    className={`flex-1 h-[40px] rounded-[10px] border border-[#e4e5e7] focus:border-[${t.primary}] px-[14px] font-medium text-[13px] text-[#212326] placeholder:text-[#9298a0] focus:outline-none`}
                                />

                                <button
                                    onClick={addSpecial}
                                    className="px-[16px] h-[40px] rounded-[10px] text-white font-semibold text-[13px]"
                                    style={{ backgroundColor: t.primary }}
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CurrentSpecialsCard;